import { treatErrorAs, handleErrorsInQueue } from 'api/errors';
import { scriptTypes } from 'constants/scripts';
import { errorLevel } from 'constants/errors';

import { datadogRum } from '@datadog/browser-rum';

// Finders
import { findActivityDefaultDates, findActivitiesForFieldUuid } from 'activities/finders';
import { findFieldBoundariesByFieldUuid } from 'boundaries/finders';
import { findProductFeaturesByFieldUuid } from 'product-features/finders';
import {
  findAllScriptsForFieldId,
  findScriptById,
  findScriptListForFieldId,
  findScriptsSummariesForFieldId
} from 'scripts/finders';
import { findProductYears } from 'settings/finders';

// selectors
import {
  getBaseManagementZones,
  getScriptBasedManagementZones,
  getImportedManagementZones
} from 'manual/selectors';

// Selectors
import {
  getProductYearForScript,
  getScriptsForFieldId,
  getScriptTypeForScript
} from 'scripts/selectors';
import { cameFromSubfieldN } from 'settings/selectors';
import { areScriptsLoadImprovementsEnabled } from 'user/selectors';

// Action Creators
import {
  completeActiveProductYearLoading,
  completeProductFeaturesLoading,
  completeBoundaryLoading,
  completeScriptListLoading,
  completeZoneLoading
} from 'api/loading';
import {
  clearActiveScriptType,
  setActiveFieldIdentifier,
  setActiveProductYear,
  setActiveScriptId,
  setActiveScriptType
} from 'current';
import {
  setBaseManagementZones,
  setScriptBasedManagementZones,
  setImportedManagementZones
} from 'manual';

// Utils
import {
  determineActiveProductYear,
  determineActiveScriptId,
  determineStartingView
} from 'util/env';

/** Loads field and associated data. Can be called during initial load of data, or again later after things are
  already loaded. ignoreRoute flag can be set to true to avoid using information in the route URL, which is useful
  when the URL is meant to update *AFTER* everything is loaded.
  */
export const loadFieldDataFullPayload = ({ dispatch, getState, fieldId, fieldUuid, ignoreRoute = false }) => {
  let scriptId;
  return Promise.resolve()
  .then( () => {
    dispatch(setActiveFieldIdentifier({ fieldId, fieldUuid }));
    // set to null in case we just switched from another field --
    // we don't want an active script hanging around from a different field
    dispatch( setActiveScriptId(null) );
    // omitting from state instead of setting to null
    dispatch( clearActiveScriptType() );


    return findProductFeaturesByFieldUuid({dispatch, getState, fieldUuid})
    .catch( treatErrorAs({ severity: errorLevel.WARN }) )
    .then( () => dispatch(completeProductFeaturesLoading()) );
  })
  .then( () => Promise.all([
    (areScriptsLoadImprovementsEnabled(getState())
      ? findScriptsSummariesForFieldId({ dispatch, getState, fieldId })
      : findScriptListForFieldId({ dispatch, getState, fieldId })),

    findFieldBoundariesByFieldUuid({dispatch, getState, fieldUuid})
    .then( () => dispatch(completeBoundaryLoading()) ),

    findActivitiesForFieldUuid({ dispatch, getState, fieldUuid })
    .catch( treatErrorAs({ severity: errorLevel.WARN }) )
  ]) )
  // moving this here so that the seasons code finder can take the active field centroid
  .then(() => {
    return findProductYears({ dispatch, getState })
    .catch( treatErrorAs({ severity: errorLevel.INFO }) );
  })
  .then( () => {
    // Attempt to load one script while loading, then do the others in the background
    const scripts = getScriptsForFieldId(getState(), fieldId);

    // if we came from subfield N, we either have the script id from the url or we don't
    if (cameFromSubfieldN(getState())) {
      // don't pass all the scripts so that we just check the route
      scriptId = determineActiveScriptId({ getState });
      if( scriptId && scripts[scriptId] ) {
        return findScriptById({ dispatch, getState, fieldId, scriptId })
        .then( (script) => {
          // set the active product year and script type by the active script
          dispatch( setActiveProductYear(getProductYearForScript(script)) );
          dispatch( setActiveScriptType(getScriptTypeForScript(script)) );
          dispatch( setActiveScriptId(scriptId) ); // It's a winner!  Save it
          dispatch( completeScriptListLoading() ); // Allow the page to display
          dispatch( completeActiveProductYearLoading() );
          datadogRum.addTiming('appFullyLoaded'); 
        });
      } else {
        return null; // we can't load a script first
      }
    } else {
      // set fertility as active script type by default if no starting view in URL -- may be reset when we
      // find an active script
      dispatch( setActiveScriptType(determineStartingView({}) || scriptTypes.FERTILITY) );
      // set active product year based on full scripts list -- may be reset when we
      // find an active script
      const activeProductYear = determineActiveProductYear({ getState, scripts });
      dispatch(setActiveProductYear(activeProductYear.toString()));

      scriptId = determineActiveScriptId({ getState, ignoreRoute, scripts });
      if( scriptId && scripts[scriptId] ) {
        return findScriptById({ dispatch, getState, fieldId, scriptId })
        .then( (script) => {
          // set the active product year and script type by the active script
          dispatch( setActiveProductYear(getProductYearForScript(script)) );
          dispatch( setActiveScriptType(getScriptTypeForScript(script)) );
          dispatch( setActiveScriptId(scriptId) ); // It's a winner!  Save it
          dispatch( completeScriptListLoading() ); // Allow the page to display
          dispatch( completeActiveProductYearLoading() );
        });
      } else {
        // default back to fertility if no starting view in URL
        dispatch(setActiveScriptType(determineStartingView({}) || scriptTypes.FERTILITY));
        return null; // we can't load a script first
      }
    }
  })
  .then( () => {
    // Load remaining scripts
    return findAllScriptsForFieldId({dispatch, getState, fieldId})
    .catch( treatErrorAs({ severity: errorLevel.WARN }) )
    .then( (scripts) => {
      dispatch( completeScriptListLoading() );
      return scripts;
    });
  })
  .then(() => {
    return findActivityDefaultDates({ dispatch, getState })
    .catch( treatErrorAs({ severity: errorLevel.INFO }) );
  })
  // management zones need to be cached after scripts are loaded because
  // all the data for determining management zones depend on the constructed zones
  // from our data structure
  .then( () => {
    return Promise.all([
      getBaseManagementZones(getState()),
      getScriptBasedManagementZones(getState()),
      getImportedManagementZones(getState())
    ])
    .then( ([ baseManagementZones, scriptBasedManagementZones, importedManagementZones ]) => { // imported management zones will be set in state via processor
      dispatch(setBaseManagementZones(baseManagementZones));
      dispatch(setScriptBasedManagementZones(scriptBasedManagementZones));
      dispatch(setImportedManagementZones(importedManagementZones));
      dispatch( completeZoneLoading() ); // Allow the page to display
    })
    .catch( treatErrorAs({ severity: errorLevel.INFO }));
  })
  .catch( treatErrorAs({ severity: errorLevel.BLOCK, andStopHere:true }) )
  .then( () => handleErrorsInQueue() );
};

export default loadFieldDataFullPayload;
